import { routerMiddleware, RouterState } from 'connected-react-router'
import { createBrowserHistory } from 'history'
import { applyMiddleware, createStore, Dispatch as ReduxDispatch } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction'
import createSagaMiddleware from 'redux-saga'
import { ACCOUNT_PAGE_STORE_ID, AccountPageState } from 'scenes/Account/duck/types'
import { isProductionEnvironment } from 'utils-next/env'

import { FLASH_NOTICE_STORE_ID, FlashNoticeState } from '../components/FlashNotice/duck/types'
import { DataState } from '../data/types'
import { DASHBOARD_STORE_ID, DashboardState } from '../scenes/Dashboard/duck/types'
import { RootAction } from './actions'
import rootSaga from './operations'
import { rootReducerFactory } from './reducers'

export type RootState = SharedRouterState & InternalRootState

type SharedRouterState = {
  readonly router: RouterState
}

export type InternalRootState = SharedDataState & PrivateState

type SharedDataState = {
  readonly data: DataState
}

type PrivateState = {
  readonly scenes: {
    readonly [DASHBOARD_STORE_ID]: DashboardState
    readonly [ACCOUNT_PAGE_STORE_ID]: AccountPageState
  }
  readonly components: {
    readonly [FLASH_NOTICE_STORE_ID]: FlashNoticeState
  }
}

export type Dispatch = ReduxDispatch<RootAction>

// Only the root store will have direct access to the router state.
export const getRouterState = ({ router }: RootState) => router

export const history = createBrowserHistory()

const configureStore = (initialState?: RootState) => {
  const composeEnhancers = composeWithDevTools({
    serialize: true,
  })
  const middlewares = []

  const sagaMiddleware = createSagaMiddleware()
  middlewares.push(sagaMiddleware)

  middlewares.push(routerMiddleware(history))

  const newStore = createStore(
    rootReducerFactory(history),
    initialState!,
    composeEnhancers(applyMiddleware(...middlewares))
  )
  sagaMiddleware.run(rootSaga)

  if (!isProductionEnvironment() && module.hot) {
    module.hot.accept('./reducers', () => {
      const nextRootReducer = require('./reducers').rootReducerFactory
      newStore.replaceReducer(nextRootReducer(history))
    })
  }

  return newStore
}
export const store = configureStore()
